import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListFlowsResult } from '@whinerp/client';
import { EmptyState, ErrorState, Num, Skeleton, type Column } from '@whinerp/ui';
import { formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-51 Journeys list.
 *
 * Journeys had a builder at /journeys/:id and no list: a saved journey was
 * reachable only by typing its id. A record type with no list is a record
 * type nobody can find.
 *
 * A journey and an automation are the same table with different palettes, so
 * this asks for one palette rather than showing both and letting somebody
 * open a flow the builder beside it cannot draw.
 */

type Row = ListFlowsResult['flows'][number];

function JourneysListScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({ field: 'updated', direction: 'desc' });
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const query = useQuery<ListFlowsResult, ApiError>({
    queryKey: ['flows', 'journey'],
    queryFn: () => api.listFlows({ query: { palette: 'journey' } }),
  });

  /*
   * Narrowed and ordered here rather than by the server.
   *
   * A workspace has tens of these, not tens of thousands. Paging them would
   * mean a cursor, a total and a page size describing a list that fits on one
   * screen - and the toolbar's search is honest about what it does: it
   * narrows what is already loaded.
   */
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = query.data?.flows ?? [];
    const found = term === '' ? all : all.filter((row) => row.name.toLowerCase().includes(term));
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...found].sort((a, b) => sort.field === 'status'
        ? a.status.localeCompare(b.status) * direction
        : sort.field === 'updated'
          ? a.updatedAt.localeCompare(b.updatedAt) * direction
          : a.name.localeCompare(b.name) * direction);
  }, [query.data, search, sort]);

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('list.column.name'),
        sortField: 'name',
        essential: true,
        cell: (row) => row.name,
      },
      {
        key: 'status',
        header: t('list.column.status'),
        sortField: 'status',
        essential: true,
        cell: (row) => row.status,
      },
      {
        key: 'updated',
        header: t('list.column.updated'),
        sortField: 'updated',
        hideOnCard: true,
        cell: (row) => <Num className="text-ink-meta">{formatRelativeDate(row.updatedAt, format)}</Num>,
      },
    ],
    [t, format],
  );

  return (
    <RecordList<Row>
      title={t('list.journeys.title')}
      icon="FlowArrow"
      rowId={(row) => row.flowId}
      recordHref={(row) => `/journeys/${row.flowId}`}
      columns={columns}
      page={{ rows, total: rows.length, hasMore: false, nextCursor: null }}
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={setSearch}
      sort={sort}
      onSortChange={(field) => setSort((current) => nextSort(current, field))}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={rows.length || 1}
      onNew={() => navigate('/journeys/new')}
      onClearFilters={() => setSearch('')}
      filtered={search.trim().length > 0}
      emptyTitle={t('list.journeys.empty.title')}
      emptyBody={t('list.journeys.empty.body')}
      emptyIllustration="Path"
    />
  );
}

function JourneysListEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="FlowArrow"
      title={t('list.journeys.empty.title')}
      body={t('list.journeys.empty.body')}
      primary={{ label: t('action.new'), icon: 'Plus', href: '/journeys/new' }}
    />
  );
}

function JourneysListLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading</span>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function JourneysListError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Journeys could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-51',
  route: '/journeys',
  surface: 'page',
  title: 'Journeys list',
  // F-068, not F-067. A screen is attributed to the feature whose operation
  // serves it, and `listFlows` carries F-068 - the flow engine both journeys
  // and automations are built on.
  features: ['F-068'],
  entitlement: 'F-068',
  reachedBy: [{ kind: 'nav', item: 'journeys' }],
  components: ['DataTable', 'FilterChips'],
  elements: ['RecordList'],
  states: {
    empty: JourneysListEmpty,
    loading: JourneysListLoading,
    error: JourneysListError,
  },
  component: JourneysListScreen,
});

export default screen;
